import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  ActivityDays,
  HELP_ARTICLE_LABEL,
  HelpFeedbackItem,
  HelpSummary,
  PLATFORM_LABEL,
  getHelpFeedback
} from '../../api/services/activityService';
import { Avatar, Card, FilterChip, KpiCard, SectionLabel, StatusPill } from '../../components/ui/dg';

/**
 * Support › Aide : les réponses à « Cette information résout-elle votre
 * problème ? » sous les articles d'aide de l'app (API migration 081), avec le
 * compte qui a répondu. Un « Non » est quelqu'un que l'article n'a pas aidé :
 * le nom mène à sa fiche, et l'app lui a proposé d'écrire au support.
 */

const PERIODS: { days: ActivityDays; label: string }[] = [
  { days: 7, label: '7 j' },
  { days: 30, label: '30 j' },
  { days: 90, label: '90 j' }
];

const pct = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 100) : 0);
const when = (iso: string) => new Date(iso).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const LANG_LABEL: Record<string, string> = { fr: 'Français', ar: 'العربية', en: 'English' };

const HelpFeedback = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [days, setDays] = useState<ActivityDays>(30);
  const [answer, setAnswer] = useState<'all' | 'yes' | 'no'>('all');
  const [article, setArticle] = useState<string>('all');
  const [items, setItems] = useState<HelpFeedbackItem[]>([]);
  const [summary, setSummary] = useState<HelpSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getHelpFeedback(token, {
        days,
        article: article === 'all' ? undefined : article,
        helpful: answer === 'all' ? undefined : answer === 'yes'
      });
      setItems(data.items);
      setSummary(data.summary);
    } catch (e: any) {
      setError(e?.message || 'Réponses indisponibles');
    } finally {
      setLoading(false);
    }
  }, [token, days, article, answer]);

  useEffect(() => { load(); }, [load]);

  const yes = summary.reduce((s, r) => s + r.yes, 0);
  const no = summary.reduce((s, r) => s + r.no, 0);
  const articles = Array.from(new Set([...Object.keys(HELP_ARTICLE_LABEL), ...summary.map((s) => s.article)]));
  const ranked = [...summary].filter((r) => r.yes + r.no > 0).sort((x, y) => pct(x.yes, x.yes + x.no) - pct(y.yes, y.yes + y.no));
  const toReview = ranked.filter((r) => pct(r.yes, r.yes + r.no) < 60).length;
  const COLS = 'grid-cols-[minmax(0,1.4fr)_minmax(0,1.6fr)_0.6fr_minmax(0,1fr)_0.7fr_0.8fr]';

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-5 xl:grid-cols-[1fr_1fr_1fr_1.4fr]">
        <KpiCard label="Réponses" value={loading && !summary.length ? '…' : String(yes + no)} sub={`sur ${days} jours`} />
        <KpiCard label="Article utile" value={yes + no ? `${pct(yes, yes + no)} %` : '—'} valueColor="#047857" sub="ont répondu oui" subColor="#6B7280" />
        <KpiCard label="À revoir" value={String(toReview)} valueColor={toReview ? '#B45309' : undefined} sub="articles sous 60 %" subColor="#6B7280" />
        <Card className="col-span-2 px-5 py-[18px] xl:col-span-1">
          <SectionLabel>Les moins utiles</SectionLabel>
          {ranked.length === 0 ? <p className="mt-3 text-[13px] text-[#6B7280]">Pas encore de réponse.</p> : ranked.slice(0, 3).map((r) => {
            const p = pct(r.yes, r.yes + r.no);
            return (
              <button key={r.article} type="button" onClick={() => setArticle(r.article)} className="mt-2.5 flex w-full items-center gap-2.5 text-left">
                <span className="min-w-0 flex-1 truncate text-[13.5px] font-semibold text-[#1E293B]">{HELP_ARTICLE_LABEL[r.article] || r.article}</span>
                <span className="h-1.5 w-[90px] rounded-full bg-[#EEF0F5]"><span className="block h-1.5 rounded-full" style={{ width: `${p}%`, background: p < 60 ? '#F59E0B' : '#10B981' }} /></span>
                <b className="w-[38px] text-right text-[12.5px]">{p} %</b>
              </button>
            );
          })}
        </Card>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {PERIODS.map((p) => <FilterChip key={p.days} active={days === p.days} onClick={() => setDays(p.days)}>{p.label}</FilterChip>)}
        <span className="w-3" />
        <FilterChip active={answer === 'all'} onClick={() => setAnswer('all')}>Oui et non</FilterChip>
        <FilterChip active={answer === 'no'} count={no || null} countTone="red" onClick={() => setAnswer('no')}>Seulement non</FilterChip>
        <span className="flex-1" />
        <select aria-label="Article" value={article} onChange={(e) => setArticle(e.target.value)}
          className="h-8 rounded-full border border-[#E5E7EB] bg-white px-3 text-[12.5px] font-bold text-[#4B5563] focus:outline-none">
          <option value="all">Tous les articles</option>
          {articles.map((a) => <option key={a} value={a}>{HELP_ARTICLE_LABEL[a] || a}</option>)}
        </select>
      </div>

      <Card className="overflow-hidden">
        {error ? (
          <div className="px-6 py-10 text-center text-[13px] text-[#B91C1C]">{error}<button type="button" onClick={load} className="mt-2 block w-full font-bold text-[#4F46E5]">Réessayer</button></div>
        ) : loading ? (
          <div className="space-y-2 p-4">{[0, 1, 2, 3].map((i) => <div key={i} className="h-11 animate-pulse rounded-xl bg-[#EEF0F5]" />)}</div>
        ) : items.length === 0 ? (
          <p className="px-6 py-12 text-center text-[13px] text-[#6B7280]">Aucune réponse sur la période.</p>
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-[720px]">
              <div className={`grid ${COLS} gap-3 px-6 pb-2.5 pt-4`}>
                {['Compte', 'Article', 'Utile', 'Téléphone', 'Langue', 'Date'].map((h) => <SectionLabel key={h}>{h}</SectionLabel>)}
              </div>
              {items.map((item) => (
                <div key={item.id_feedback} className={`grid ${COLS} items-center gap-3 border-t border-[#EEF0F4] px-6 py-3`}>
                  {item.user_id ? (
                    <button type="button" onClick={() => navigate(`/users?compte=${item.user_id}`)} className="flex min-w-0 items-center gap-3 text-left">
                      <Avatar name={item.user_name || item.user_email} size={32} tone={item.helpful ? 'green' : 'orange'} />
                      <b className="truncate text-[14px] font-bold text-[#1E293B] hover:text-[#4F46E5]">{item.user_name || 'Compte sans nom'}</b>
                    </button>
                  ) : <span className="text-[13px] text-[#9CA3AF]">Compte supprimé</span>}
                  <span className="truncate text-[13.5px] text-[#1E293B]">{HELP_ARTICLE_LABEL[item.article] || item.article}</span>
                  <span><StatusPill tone={item.helpful ? 'green' : 'red'}>{item.helpful ? 'Oui' : 'Non'}</StatusPill></span>
                  <span className="truncate text-[13px] text-[#6B7280]">{item.platform ? `${PLATFORM_LABEL[item.platform] || item.platform}${item.app_version ? ` · ${item.app_version}` : ''}` : '—'}</span>
                  <span className="text-[13px] text-[#1E293B]">{item.lang ? LANG_LABEL[item.lang] || item.lang : '—'}</span>
                  <span className="text-[12.5px] text-[#6B7280]">{when(item.created_at)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {!loading && items.length > 0 && <div className="border-t border-[#EEF0F4] px-6 py-3.5 text-[13px] text-[#6B7280]">{items.length} {items.length > 1 ? 'réponses' : 'réponse'}</div>}
      </Card>
    </div>
  );
};

export default HelpFeedback;
